// A second instance of the voice's wasm on the main thread, for poses.
//
// The scene draws every moving part from the module's pure pose_at(θ, slow)
// (ADR 0002). It cannot ask the worklet, which answers only between blocks, so
// the page builds the same voice here from the same init and calls pose_at
// directly. The same wasm bytes on the same inputs give the same bits as the
// worklet's voice and as the native build (web/tests/equality/).

import type { Powertrain, TimelineEvent } from "./protocol";
import { SAMPLE_RATE } from "./protocol";
import {
  KIND_JOB,
  KIND_LIVE,
  SLOW_IN,
  STATE_FIELDS,
  checkAbi,
  liveSpec,
  newVoice,
  type PsimExports,
  type VoiceInfo,
} from "./wasm-abi";

/** What the pose instance is built from: the same as the voice's init. */
export interface PoseSource {
  /** The voice's WebAssembly bytes (loadVoiceWasm()). */
  wasm: ArrayBuffer;
  /** The engine bundle (loadEngineBundle()), as text or UTF-8 bytes; none by default. */
  bundle?: Uint8Array | string;
  /** The voice's assets (loadListenerAssets()); only the IRs are read. The voice needs every IR its engine's listeners name. */
  assets?: readonly { id: string; kind: string; bytes: ArrayBuffer }[];
  /** A render job file (TOML), or else a live session's powertrain. */
  job?: Uint8Array | string;
  powertrain?: Powertrain;
  timeline?: TimelineEvent[];
  sampleRate?: number;
}

const toBytes = (text: Uint8Array | string | undefined): Uint8Array =>
  text === undefined ? new Uint8Array(0) : typeof text === "string" ? new TextEncoder().encode(text) : text;

export class PoseInstance {
  /** What the voice runs: its module, pose drivers and slow-state names. */
  readonly info: VoiceInfo;

  private readonly ex: PsimExports;
  private readonly handle: number;

  private constructor(ex: PsimExports, handle: number, info: VoiceInfo) {
    this.ex = ex;
    this.handle = handle;
    this.info = info;
  }

  static async create(source: PoseSource): Promise<PoseInstance> {
    const { instance } = await WebAssembly.instantiate(source.wasm, {});
    const ex = checkAbi(instance.exports as unknown as PsimExports);
    let kind = KIND_JOB;
    let spec = toBytes(source.job);
    if (source.job === undefined) {
      if (source.powertrain === undefined) throw new Error("a pose instance needs a job or a powertrain");
      kind = KIND_LIVE;
      spec = toBytes(JSON.stringify(liveSpec(source.powertrain, source.timeline, source.sampleRate ?? SAMPLE_RATE)));
    }
    const irs = (source.assets ?? []).filter((a) => a.kind === "ir").map((a) => ({ id: a.id, bytes: new Uint8Array(a.bytes) }));
    const { handle, info } = newVoice(ex, kind, spec, toBytes(source.bundle), irs);
    return new PoseInstance(ex, handle, info);
  }

  /** The pose-driver names, in the order of poseAt's values. */
  get drivers(): string[] {
    return this.info.pose;
  }

  /** The slow state at frame 0, in the order of info.slow. */
  initialSlow(): Float64Array {
    const state = new Float64Array(this.ex.memory.buffer, this.ex.psim_state_ptr(this.handle), STATE_FIELDS + this.info.slow.length);
    return state.slice(STATE_FIELDS);
  }

  /**
   * The pose for crank angle `theta` (radians in [0, 4π)) and slow state `slow`
   * (values in the order of info.slow, as telemetry carries them), written into
   * `out` (one value per driver) and returned.
   */
  poseAt(theta: number, slow: ArrayLike<number> = [], out = new Float64Array(this.info.pose.length)): Float64Array {
    const ex = this.ex;
    const slowIn = new Float64Array(ex.memory.buffer, ex.psim_slow_in_ptr(this.handle), SLOW_IN);
    for (let i = 0; i < SLOW_IN; i++) slowIn[i] = i < slow.length ? slow[i]! : 0;
    const ptr = ex.psim_pose_at(this.handle, theta);
    out.set(new Float64Array(ex.memory.buffer, ptr, this.info.pose.length));
    return out;
  }

  /** Frees the voice. The instance must not be used after. */
  free(): void {
    this.ex.psim_free(this.handle);
  }
}
